import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Option } from '../../../core';
import Button from '../../ui/Button';
import Form from '../../ui/Form';
import SelectInput from '../../ui/SelectInput';
import { RefferingPractitionerFormInputs } from '../types';
import { renderDefaultRefferingPractitionerInputValues } from './helper';
import RefferingPractitionerFormContext from './RefferingPractitionerFormContext';
import RefferingPractitionerInputs from './RefferingPractitionerInputs';

export interface RefferingPractitionerFormProps {
  values?: RefferingPractitionerFormInputs;
  editing: boolean;
  onSelectPractitioner?: (data?: Option) => void;
  onSubmit?: (data: RefferingPractitionerFormInputs) => void;
  onCancel?: () => void;
  loadPractitioners?: any;
  practitioners?: Option[];
  selectedPractitioner?: RefferingPractitionerFormInputs | null;
  hpdUrl?: string;
  isSubmitOnEnter?: boolean;
  showDifferentLocationWarning?: boolean;
  onClickAdd?: (selectedOption?: Option) => void;
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CreateNewWrapper = styled(Button)`
  align-self: flex-end;
  justify-self: end;
  height: 36px;
  margin-right: 20px;
`;

// this is necessary for gatsby to build using the window object
const isBrowser = typeof window !== 'undefined';

const RefferingPractitionerForm = ({
  values,
  onCancel,
  onSubmit,
  editing,
  loadPractitioners,
  onSelectPractitioner,
  selectedPractitioner,
  hpdUrl,
  isSubmitOnEnter,
  showDifferentLocationWarning,
}: RefferingPractitionerFormProps) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>();
  const referralType = values?.referralType;
  const data = renderDefaultRefferingPractitionerInputValues(values);
  const editFormValue = renderDefaultRefferingPractitionerInputValues({...selectedPractitioner, referralType});

  useEffect(() => {
    if (selectedPractitioner && editing) {
      setShowEditForm(true);
    }
  }, [selectedPractitioner, editing]);

  const handleCancel = () => {
    onSelectPractitioner && onSelectPractitioner();
    setSelectedOption(null);
    setShowEditForm(false);
    onCancel && onCancel();
  };

  const handleSubmit = (data: RefferingPractitionerFormInputs) => {
    onSelectPractitioner && onSelectPractitioner();
    setShowEditForm(false);
    onSubmit && onSubmit(data);
  };

  return (
    <RefferingPractitionerFormContext.Provider
      value={{
        selectedPractitioner,
        values: data,
        editing,
        showDifferentLocationWarning,
      }}
    >
      {editing ? (
        <React.Fragment>
          <TitleContainer>
            <Form.RowLabel>New Referrer</Form.RowLabel>
          </TitleContainer>
          <React.Fragment>
            <Form.Row column={2} margin="0 0 20px 0">
              <SelectInput
                placeholder="Search For a Practitioner"
                label="Change Referrer"
                variant="primary"
                isAsync
                isClearable
                loadOptions={loadPractitioners}
                onChange={(newSelectedOption: Option) => {
                  setSelectedOption(newSelectedOption);
                  onSelectPractitioner && onSelectPractitioner(newSelectedOption);
                }}
                value={selectedOption}
              />
              <CreateNewWrapper
                size="small"
                variant="secondary"
                onClick={() => isBrowser && window.open(hpdUrl, '_blank')}
              >
                Create New
              </CreateNewWrapper>
            </Form.Row>
            {showEditForm && (
              <Form
                values={editFormValue}
                onCancel={handleCancel}
                onSubmit={handleSubmit}
                edit={editing}
                isSubmitOnEnter={isSubmitOnEnter}
              >
                <RefferingPractitionerInputs />
              </Form>
            )}
          </React.Fragment>
        </React.Fragment>
      ) : (
        <Form
          values={data}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          edit={editing}
          isSubmitOnEnter={isSubmitOnEnter}
        >
          <RefferingPractitionerInputs />
        </Form>
      )}
    </RefferingPractitionerFormContext.Provider>
  );
};

export default RefferingPractitionerForm;
